// retunePolicy — live-buffer-resilience (Phase 6). Pure decision module:
// given a fatal PlayerError kind, what recovery action (if any) should the
// app-shell's rewind self-heal take?
//
// Extracted out of App.vue's `maybeRecoverRewindSession` so the "which kind
// gets auto-retuned" decision is unit-testable in isolation from the DOM /
// Pinia / router wiring that function also does. The 30s recovery throttle
// and the actual retune/cleanup orchestration STAY in App.vue — this module
// only answers the policy question.
//
// 2026-07-05 incident: the OLD inline logic force-retuned on ANY
// 'media-network' fatal, including mid-outage while the live-buffer service
// was actively respawning its sink. The backend now serves a frozen composed
// manifest during that outage (never a 404), so a network error there no
// longer means "the buffer is gone" — force-retuning wiped a healthy
// in-progress reconnect and snapped the timeline back ~30s (the exact
// `player.backward_jump` regression this feature fixes). 'media-network'
// now falls through to the sticky-toast path with a manual Retry action
// instead.

import type { LivePlaybackMode } from '@/lib/api';
import type { PlayerErrorKind } from './playerErrors';

/**
 * The live delivery modes backed by a SERVER-SIDE playback session that can
 * terminally expire and that answer a dead identity with the recoverable
 * `403` session-loss status.
 *
 * - `rewind`   — the live-buffer sink's session; 403 on a swept registry entry.
 * - `transcode` — the HLS transcode session; 403 `session_expired` since the
 *   2026-09-03 incident work (it used to be an ambiguous `404 Session not
 *   found`, indistinguishable from "playlist not ready yet").
 * - `proxy`   — ADDED in Phase 1b of the playback-reliability plan. It was
 *   excluded until then for a precise reason: every `touchPlayback()` in
 *   `server/routes/proxy.js` ran after `res.send()` and discarded both the
 *   return value and the throw, so an expired or foreign session was swallowed
 *   and the provider stream kept being served — a proxy 403 was not an
 *   authoritative terminal signal because the route could not produce one.
 *   It now gates on the registry BEFORE any upstream contact and answers
 *   `403 session_expired` with the `X-Nodecast-Playback-Error` header, the
 *   same contract rewind and transcode use.
 *
 * Deliberately EXCLUDED:
 * - `remux`  — no session identity of its own; its `p=` touch is still
 *   best-effort keepalive, not a gate.
 * - `catchup` — VOD-shaped playback, not a live tune; recovery would re-tune to
 *   the live edge and silently change what the viewer is watching.
 */
const SESSION_BACKED_LIVE_MODES: ReadonlySet<LivePlaybackMode> = new Set<LivePlaybackMode>([
    'rewind',
    'transcode',
    'proxy',
]);

/**
 * Whether a terminal session-loss verdict on this mode may trigger ONE fresh
 * live-session acquisition. `null`/unknown modes are never eligible.
 */
export function isSessionBackedLiveMode(mode: LivePlaybackMode | null | undefined): boolean {
    return mode != null && SESSION_BACKED_LIVE_MODES.has(mode);
}

export type RetuneAction =
    /** Re-run the tune-in flow from scratch (plain re-tune). */
    | 'retune'
    /** Tear down the current player instance fully (release the session), THEN retune. */
    | 'release-and-retune'
    /** No automatic recovery — fall through to the normal sticky-toast path. */
    | 'none';

/**
 * Map a fatal error kind to the recovery action the rewind self-heal should
 * take. Only meaningful for kinds that can plausibly occur on an active
 * rewind session; every other kind maps to `'none'`.
 */
export function retuneActionForErrorKind(kind: PlayerErrorKind): RetuneAction {
    switch (kind) {
        case 'session-lost':
            // The registry entry is gone (TTL sweep / buffer torn down) — a
            // plain re-tune re-mints a fresh session.
            return 'retune';
        case 'media-decode':
            // Browser attachment recovery is owned by NodecastPlayer. Releasing
            // a shared sink cannot repair one viewer's decoder.
            return 'none';
        case 'stream-limit':
            // Close the rejected reader; the tune waits and retries with
            // backoff while the loading overlay owns the feedback.
            return 'release-and-retune';
        case 'media-network':
            // 2026-07-05 fix: no longer force-retunes. The composed manifest
            // stays live (frozen tail, no 404) through a buffer outage now,
            // so a network error here is not proof the buffer is gone.
            return 'none';
        default:
            return 'none';
    }
}
